import * as React from 'react';
import Box from '@mui/material/Box';
import { IconButton, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useDispatch } from 'react-redux';
import {
  BLOCK_MIN_WIDTH,
  COMMON_PADDING,
  FIELD_ICO_COLOR,
  FIELD_TXT_COLOR,
  FLD_BLOCK_HEIGHT,
  PANEL_COLOR,
  ROUTE_BLOCK_HEIGHT,
} from './constants';
import RouteItem from './RouteItem';
import ButtonWithMenu from './ButtonWithMenu';
import DetailsDialog from './DetailsDialog';
import { logisticTasksBalanceResourceActions } from '../../../entities/logistic/logisticTasks/redux/logisticTasksBalance.slice';
import CalculatingTransport from './components/CalculatingTransport/CalculatingTransport';
import calculatingTransportVehicleType from './types/calculating-transport-vehicle.type';
import FieldHarvesterStatuses from './components/FieldHarvesterStatuses';
import FieldIcon from './components/FieldIcon/FieldIcon';
import FieldFooter from './ui/FieldItem/FieldFooter';
import formatFloatNum from '../../../shared/utils/floatFormatter';
import RowWrapper from './ui/FieldItem/RowWrapper';
import FieldWrapper from './ui/FieldItem/FieldWrapper';

export default function FieldItem(props) {
  const dispatch = useDispatch();
  const {
    bgColor,
    harvesters,
    fieldName,
    area,
    cropType,
    availableVehicles,
    id,
    routes,
    latest,
    availableHarvesters,
    sx,
    percentHarvested,
    cropPlanCapacity,
    cargoVolumeGraphics,
    ...other
  } = props;

  const [selectedHarvester, setSelectedHarvester] = React.useState({ item: null, onOk: null });
  const [fieldItemDetails, setFieldItemDetails] = React.useState(null);

  const openHarvesterDialog = (item, onOk, okLabel) => {
    setSelectedHarvester({ item, onOk, okLabel });
  };

  const closeHarvesterDialog = () => {
    setSelectedHarvester({ item: null, onOk: null });
  };

  const openFieldDetailsDialog = () => {
    setFieldItemDetails({
      id,
      fieldName,
      area,
      cropType,
      cropPlanCapacity,
      ...other,
    });
  };

  const closeFieldDetailsDialog = () => {
    setFieldItemDetails(null);
  };

  const addHarvester = (hv) => {
    dispatch(
      logisticTasksBalanceResourceActions.addHarvesterToField({
        id,
        hv,
      }),
    );
  };

  const removeHarvester = (hv) => {
    dispatch(
      logisticTasksBalanceResourceActions.removeHarvesterFromField({
        id,
        hv,
      }),
    );
  };

  const activeHarvestersIds = harvesters?.map((e) => e?.vehicleId);

  return (
    <>
      <Box
        sx={{
          bgcolor: PANEL_COLOR,
          p: `${COMMON_PADDING}px ${latest ? COMMON_PADDING : COMMON_PADDING / 2}px ${COMMON_PADDING}px ${COMMON_PADDING / 2}px`,
          borderRadius: `0 ${latest ? 8 : 0}px ${latest ? 8 : 0}px 0`,
          minWidth: `${BLOCK_MIN_WIDTH}px`,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: fieldName ? bgColor : 'transparent',
            borderRadius: '4px',
            height: `${FLD_BLOCK_HEIGHT}px`,
            ...sx,
          }}
        >
          {id !== undefined && (
            <FieldWrapper>
              <FieldFooter>
                <Typography
                  title={`${fieldName} ${area} ${cropType}`}
                  sx={{
                    fontSize: '0.75rem',
                    color: FIELD_TXT_COLOR,
                    textWrap: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    margin: '0 6px',
                    lineHeight: 1,
                    paddingTop: '8px',
                  }}
                >
                  {fieldName}
                  <br />
                  {cropPlanCapacity}т{percentHarvested !== null ? `, ${formatFloatNum(percentHarvested)}%` : ''}
                  <br />
                  {cropType},{area.replace(/\s+/g, '')}
                  <br />
                </Typography>
                <FieldIcon geozoneId={props?.geozoneId} />
                <RowWrapper>
                  <IconButton
                    sx={{ p: 0 }}
                    title="Информация о поле"
                    id="basic-button"
                    aria-controls={fieldItemDetails ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={fieldItemDetails ? 'true' : undefined}
                    onClick={openFieldDetailsDialog}
                  >
                    <SettingsIcon sx={{ color: FIELD_ICO_COLOR, p: 0 }} />
                  </IconButton>
                  <CalculatingTransport
                    id={id}
                    fieldName={fieldName}
                    producerId={props?.geozoneId}
                    transportVolumePoint={cargoVolumeGraphics?.[props?.geozoneId]?.transportVolumePoint}
                  />
                  <Box sx={{ display: 'flex' }}>
                    <ButtonWithMenu
                      sx={{
                        '& .MuiIconButton-sizeMedium': {
                          pl: 0,
                          m: 0,
                        },
                      }}
                      items={availableHarvesters}
                      onItemClick={(item) =>
                        openHarvesterDialog(item, () => addHarvester(item), 'Добавить комбайн на поле')
                      }
                      onAddItemClick={(item) => addHarvester(item)}
                      title="Добавить комбайн"
                    >
                      <AddCircleIcon sx={{ color: '#7fb63f', p: 0 }} />
                    </ButtonWithMenu>
                  </Box>
                </RowWrapper>
              </FieldFooter>
              <DetailsDialog type="field" item={fieldItemDetails} closeItemDialog={closeFieldDetailsDialog} />
              <Stack direction="row" justifyContent="center">
                {!!activeHarvestersIds?.length && <FieldHarvesterStatuses ids={activeHarvestersIds} />}
              </Stack>
              <ButtonWithMenu
                sx={{
                  '& .MuiButton-root': {
                    padding: '0 2px',
                    width: '100%',
                    margin: 0,
                    color: 'text.primary',
                    minWidth: 0,
                  },
                  margin: 0,
                  padding: 0,
                }}
                type="button"
                items={harvesters}
                onItemClick={(item) => openHarvesterDialog(item, () => removeHarvester(item), 'Удалить комбайн с поля')}
                onRemoveItemClick={(item) => removeHarvester(item)}
                title="Комбайны на поле"
              >
                <Box
                  sx={{
                    background: '#ddeec8',
                    borderRadius: '4px',
                    width: '100%',
                    minHeight: '32px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      textAlign: 'center',
                    }}
                  >
                    {harvesters?.length || '  '}
                  </Typography>
                </Box>
              </ButtonWithMenu>
            </FieldWrapper>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          bgcolor: PANEL_COLOR,
          p: `${COMMON_PADDING}px ${latest ? COMMON_PADDING : COMMON_PADDING / 2}px ${COMMON_PADDING}px ${COMMON_PADDING / 2}px`,
          borderRadius: `0 ${latest ? 8 : 0}px ${latest ? 8 : 0}px 0`,
          display: 'flex',
          gap: `${COMMON_PADDING}px`,
          mt: `${COMMON_PADDING}px`,
          height: `${ROUTE_BLOCK_HEIGHT + 2 * COMMON_PADDING}px`,
          justifyContent: 'center',
        }}
      >
        {routes?.map((route) => (
          <RouteItem
            key={`${id}_${route.warehouseId}`}
            fieldId={id}
            availableVehicles={availableVehicles}
            {...route}
            vehicles={route.vehicles}
          />
        ))}
      </Box>
      <DetailsDialog
        type="harvester"
        item={selectedHarvester?.item}
        onOk={selectedHarvester?.onOk}
        closeItemDialog={closeHarvesterDialog}
        okLabel={selectedHarvester?.okLabel}
      />
    </>
  );
}

FieldItem.propTypes = {
  id: PropTypes.string.isRequired,
  bgColor: PropTypes.string,
  harvesters: PropTypes.arrayOf(PropTypes.shape(calculatingTransportVehicleType)),
  availableHarvesters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      model: PropTypes.string,
      regNum: PropTypes.string,
    }),
  ),
  availableVehicles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      model: PropTypes.string,
      regNum: PropTypes.string,
    }),
  ),
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      warehouseId: PropTypes.string.isRequired,
      warehouseIndex: PropTypes.number.isRequired,
    }),
  ),
  warehouses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  cargoVolumeGraphics: PropTypes.arrayOf(PropTypes.shape()),
  fieldName: PropTypes.string,
  area: PropTypes.string,
  cropType: PropTypes.string,
  geozoneId: PropTypes.number.isRequired,
  latest: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.any,
  vehicles: PropTypes.arrayOf({ vehicleId: PropTypes.number }).isRequired,
  percentHarvested: PropTypes.number,
  cropPlanCapacity: PropTypes.number,
};

FieldItem.defaultProps = {
  bgColor: '#bbdf93',
  latest: false,
  harvesters: [],
  availableHarvesters: [],
  availableVehicles: [],
  routes: [],
  warehouses: [],
  cargoVolumeGraphics: [],
  fieldName: '',
  area: '',
  cropType: '',
  sx: {},
  percentHarvested: null,
  cropPlanCapacity: null,
};
